import { useState, useEffect, FC, ChangeEvent, useContext } from 'react';
import { SwitchOnChangeData } from '@fluentui/react-switch';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Stack, StackItem, TextField } from '@fluentui/react';
import { Button, Divider, Text, Body1, Caption1, FluentProvider, Tooltip, Switch, Radio, Body1Stronger, Body1Strong } from '@fluentui/react-components';
import { SideMenu } from '../../components/side_menu/inline_side_menu.component';
import { Breadcrumbs } from '../../components/breadcrumbs/breadcrumbs.component';
import FullScreenButton from '../../components/fullscreen/fullScreen.component';
import { usePermissionStore } from '../../Store/permissionStore';
import { getSSOConfig, insertSSOConfig, updateSSOConfig, clearSSOConfig, defaultConfig, updateSSOStatus } from '../../api/sing_in/ssoConfigOperations';
import { DynamicBoxingCSS, formOverflowDivider, heightResponsive, dashboardTheme, parentHome, primaryIcon, secondaryIcon, SubSectionResponsive, tableDiv, BreadcrumbsBorder } from '../../styles/customStyles';
import { ArrowClockwise20Regular, ArrowDownload20Regular, Info16Filled, Info20Filled, } from '@fluentui/react-icons';
import { SkeletonDataTable } from '../../components/skeleton/skeletonDataTable';
import { OverflowSetCustomExample } from '../../components/side_menu/overFlowSet.component';
import { ToastComponent } from '../../components/toast/toast.component';
import DialogBox from '../../components/dialog/dialog.component';
import { SSOPages } from '../../components/side_menu/InlineSidenav';
import { GlobalContext } from '../../hooks/globalContext';

interface iOAuthConfig {
  company_name: string;
  idp_full_name: string;
  client_id: string;
  client_secret: string;
  scope: string;
  oidc_endpoint: string;
  domain_name: string;
  sso_status: number;
  // sso_type: string;
};

interface iSAMLConfig {
  company_name: string;
  idp_full_name: string;
  client_id: string;
  client_secret: string;
  extra_endpoints: string;
  domain_name: string;
  sso_status: number;
  // sso_type: string;
};

type messageType = {
  type: 'success' | 'error' | 'warning';
  text: string
};

export const SSOConfiguration: FC = () => {
  const OAuthConfig = {
    company_name: '', idp_full_name: '', client_id: '', client_secret: '', scope: '', oidc_endpoint: '', domain_name: '', sso_status: 0,
    /* sso_type: '' */
  };
  const SAMLConfig = {
    company_name: '', idp_full_name: '', client_id: '', client_secret: '', extra_endpoints: '', domain_name: '', sso_status: 0
  };

  const [isToast, setIsToast] = useState(false);
  const [isRefresh, setIsRefresh]: any = useState(false);
  const [refreshToast, setRefreshToast]: any = useState(false);
  const [isSubmitDisabled, setSubmitDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const [leftOpenSubMenu, setleftOpenSubMenu] = useState(true);
  const [message, setMessage] = useState<messageType | null>(null);
  const [configId, setConfigId] = useState<string | null>(null);
  const [selectedAuthType, setSelectedAuthType] = useState('OAUTH');
  const [formData, setFormData] = useState<iOAuthConfig>(OAuthConfig);
  const [SAMLFormData, setSAMLFormData] = useState<iSAMLConfig>(SAMLConfig);
  const [config, setConfig] = useState<any>(defaultConfig);

  const { permissions } = usePermissionStore();
  const handle = useFullScreenHandle();

  const contextValue: any = useContext(GlobalContext);
  const { tenantName } = contextValue;

  // Initial load
  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const domain_name = window.location.hostname;
      const { data, status } = await getSSOConfig(selectedAuthType, domain_name);
      if (status === 'success' && data.length > 0) {
        if (data[0]?.sso_type !== '') {
          setSelectedAuthType(data[0]?.sso_type);
          data[0]?.sso_type === 'OAUTH' ? setFormData(data[0]) : setSAMLFormData(data[0])
        };
        setConfigId(data[0].ssoid);
        setIsDisabled(false);
        setIsLoading(false);
      } else { // Reset forms when data is empty
        setFormData(data[0]);
        setSAMLFormData({
          company_name: '', idp_full_name: '', client_id: '', client_secret: '', extra_endpoints: '', domain_name: '', sso_status: 0,
          /* sso_type: '' */
        });
        setConfigId(null);
        setIsDisabled(true);
      };
    } catch (error) {
      console.error('Error fetching config: ', error);
      setMessage({ type: 'error', text: 'Failed to load SSO configuration' });
    } finally {
      setIsLoading(false);
    };
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    isRefresh && fetchConfig();
  }, [isRefresh]);

  const handleAuthTypeChange = (key) => { setSelectedAuthType(key) };

  const validateFormData = (): boolean => {
    const { company_name, idp_full_name, client_id, client_secret, scope, oidc_endpoint } = formData;
    const errors = (message: string) => (
      setMessage({ type: "error", text: message }),
      setIsToast(true),
      setTimeout(() => setIsToast(false), 3000),
      false
    );
    return !company_name
      ? errors("Company name is required.")
      : !idp_full_name
        ? errors("IDP Full Name is required.")
        : !client_id
          ? errors("Client ID is required.")
          : !client_secret
            ? errors("Client Secret is required.")
            : (!scope && selectedAuthType !== "SAML")
              ? errors("Scope is required.")
              : ((!oidc_endpoint || !/^https?:\/\/.+/.test(oidc_endpoint)) && selectedAuthType !== "SAML")
                ? errors("A valid OIDC endpoint is required (must start with http:// or https://).")
                : true;
  };

  const handleSubmit = async () => {
    setSubmitDisabled(true);
    if (!validateFormData()) return;
    const domain_name = window.location.hostname;
    let updatedData;
    // Prepare the data with domain_name and sso_type
    (selectedAuthType === 'SAML')
      ? updatedData = { ...SAMLFormData, domain_name: domain_name, sso_type: selectedAuthType.toUpperCase(), sso_status: 0 }
      : updatedData = { ...formData, domain_name: domain_name, sso_type: selectedAuthType.toUpperCase(), sso_status: 0 };
    try {
      // Check if configuration exists in DB
      const { data } = await getSSOConfig(selectedAuthType, domain_name);
      let response;
      if (data.length === 0) {
        // No existing configuration - perform insert
        response = await insertSSOConfig(updatedData);
        console.log('Inserting new configuration: ', updatedData);
        if (response.status === 'success') {
          setMessage({ type: 'success', text: 'New configuration saved successfully!' });
          setIsDisabled(false);
          setSubmitDisabled(false);
        } else {
          setMessage({ type: 'error', text: 'New configuration insertion failed!' });
        }
        setIsToast(true);
        setTimeout(() => { setIsToast(false) }, 3000);
      } else {
        // Existing configuration - perform update
        response = await updateSSOConfig(updatedData);
        console.log('Updating existing configuration: ', updatedData);
        setMessage({ type: 'success', text: 'Configuration updated successfully!' });
        setSubmitDisabled(false);
        setMessage({
          type: response.status === 'success' ? 'success' : 'error',
          text: response.status === 'success' ? 'Configuration updated successfully!' : 'Configuration update failed!',
        });
      };
      // Update local state with the new data
      (selectedAuthType === 'SAML')
        ? setSAMLFormData(updatedData)
        : setFormData(updatedData);
    } catch (error) {
      console.error('Error saving SSO config: ', error);
      setMessage({ type: 'error', text: 'Failed to save configuration.' });
      setIsToast(true);
      setSubmitDisabled(false);
      setTimeout(() => { setIsToast(false) }, 3000);
    };
  };

  const handleSSOToggle = async (ev: ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => {
    // setConfig(prev => ({ ...prev, sso_status: data.checked ? 1 : 0 }));
    let switchButtonStatus = data.checked ? 1 : 0;
    const domain_name = window.location.hostname;
    console.log('Switch switchButtonStatus: ' + switchButtonStatus);
    (selectedAuthType !== 'SAML')
      ? setFormData(prevData => ({ ...prevData, sso_status: switchButtonStatus }))
      : setSAMLFormData(prevData => ({ ...prevData, sso_status: switchButtonStatus }));
    const configuration = { sso_status: switchButtonStatus, domain_name: domain_name, ssoid: configId }
    try {
      const { status } = await updateSSOStatus(configuration);
      console.log('Checking updateSSOStatus success: ', status);
      if (status === 'success') {
        switchButtonStatus === 1
          ? setMessage({ type: 'success', text: 'Single Sign On enabled successfully!' })
          : setMessage({ type: 'success', text: 'Single Sign On disabled successfully!' });
        setIsToast(true);
        setTimeout(() => { setIsToast(false) }, 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to enabled Single Sign On.' });
        setIsToast(true);
        setTimeout(() => { setIsToast(false) }, 3000);
      };
    } catch (error) {
      console.error('Error updating config: ', error);
      setMessage({ type: 'error', text: 'An error occurred while updating the status.' });
      setIsToast(true);
      setTimeout(() => { setIsToast(false) }, 3000);
    } finally {
      setIsLoading(false);
    };
    // console.log('Status config: ', formData);
  };

  useEffect(() => {
    console.log('Checking formData changed: ' + JSON.stringify(formData));
  }, [formData]);

  const handleClear = async () => {
    // setIsLoading(true);
    try {
      const { status, data } = await clearSSOConfig(configId);
      if (status === 'success') {
        setConfig(defaultConfig);
        fetchConfig();
        setMessage({ type: 'success', text: 'Configuration cleared successfully!' });
        setIsToast(true);
        setTimeout(() => { setIsToast(false) }, 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to clear configuration.' });
        setIsToast(true);
        setTimeout(() => { setIsToast(false) }, 3000);
      }
    } catch (error) {
      console.error('Error clearing config:', error);
      setMessage({ type: 'error', text: 'An error occurred while clearing the configuration.' });
      setIsToast(true);
      setTimeout(() => { setIsToast(false) }, 3000);
    } finally {
      setIsLoading(false);
    };
  };

  const breadcrumbData = () => (
    <StackItem grow shrink style={BreadcrumbsBorder}>
      <Stack tokens={{ childrenGap: 10 }} horizontal>
        <StackItem grow shrink>
          <Stack tokens={{ childrenGap: 5 }} horizontalAlign="start">
            <StackItem><Text /></StackItem>
            <StackItem grow shrink>
              <StackItem grow shrink>
                <Breadcrumbs items={[
                  { name: "Manage", link: "/home" },
                  { name: "Administration", link: "/manage/administration/role" },
                  { name: "SSO", link: "#" },
                  { name: "Single Sign-On Configuration", link: "#" },
                ]} />
              </StackItem>
            </StackItem>
          </Stack>
        </StackItem>
      </Stack>
    </StackItem>
  );

  const sidebarData = () => <SideMenu leftOpenSubMenu={leftOpenSubMenu} setleftOpenSubMenu={setleftOpenSubMenu} menuItems={SSOPages(`Single${" "}Sign${" "}On`)} />;

  const overflowData = () => {
    const handleRefresh = () => {
      setTimeout(() => {
        setIsLoading(false); setRefreshToast(false); setIsRefresh(false);
      }, 5000);
      setRefreshToast(true);
      setIsLoading(true);
    }

    const refreshButton = () => (
      <StackItem>
        <Button shape="circular" disabled={isLoading} appearance="subtle" size="small" onClick={handleRefresh}>
          <Stack tokens={{ childrenGap: 5 }} horizontal>
            <StackItem> <ArrowClockwise20Regular {...(isLoading ? secondaryIcon : primaryIcon)} /> </StackItem>
            <StackItem> <Caption1> Refresh </Caption1> </StackItem>
          </Stack>
        </Button>
      </StackItem>
    );

    const exportButton = () => {
      const exportToCSV = () => {
        const headers = ["Company Name", "IdP Full Name", "Client ID", "Client Secret", "Scope", "OIDC Endpoint"];

        const {
          company_name: oAuthCompany, idp_full_name: oAuthIDP, client_id: oAuthID, client_secret: oAuthSecret, scope: oAuthScope, oidc_endpoint: oAuthOIDC
        } = formData || {};
        const { company_name: samlCompany, idp_full_name: samlIDP, client_id: samlID, client_secret: samlSecret } = SAMLFormData || {};

        const data = selectedAuthType === 'OAUTH'
          ? [[oAuthCompany, oAuthIDP, oAuthID, oAuthSecret, oAuthScope, oAuthOIDC]]
          : [[samlCompany, samlIDP, samlID, samlSecret, "", ""]];

        const csvContent = [headers, ...data].map((row) => row.map((value) => `"${value}"`).join(",")).join("\n");
        const link = Object.assign(document.createElement("a"), {
          href: URL.createObjectURL(new Blob([csvContent], { type: "text/csv" })),
          download: `${tenantName}_sso.csv`
        });

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      const handleExport = () => { /* setExportBtnClicked(true) userActivity('Export', `{"summary" : "Roles List Exported"}`)*/ };

      const exportIcons = (icons: any) => (
        <Stack tokens={{ childrenGap: 5 }} horizontal>
          <StackItem> {icons} </StackItem>
          <StackItem> <Caption1> Export </Caption1> </StackItem>
        </Stack>
      );

      const permits = permissions.includes("all") || permissions.includes("overview_Fullaccess") || permissions.includes("overview_ReadandExport");

      return (
        <StackItem>
          {permits
            ? <StackItem>
              <Button shape="circular" disabled={isLoading} appearance="subtle" size="small" onClick={exportToCSV}>
                {exportIcons(<ArrowDownload20Regular {...(isLoading ? secondaryIcon : primaryIcon)} />)}
              </Button>
            </StackItem>
            :
            <Tooltip withArrow content="You do not have access" relationship="description" positioning="above">
              <Button shape="circular" appearance="subtle" size="small" disabled>
                <Stack tokens={{ childrenGap: 5 }} horizontal>
                  <StackItem><Text /></StackItem>
                  <StackItem> {exportIcons(<ArrowDownload20Regular {...secondaryIcon} />)} </StackItem>
                </Stack>
              </Button>
            </Tooltip>
          }
        </StackItem>
      );
    };

    const fullscreenButton = () => (
      <StackItem grow shrink>
        <Stack horizontalAlign="end"> <StackItem grow shrink> <FullScreenButton handle={handle} /> </StackItem> </Stack>
      </StackItem>
    );

    const overFlowComponent = {
      data:
        <>
          {refreshButton()}
          {exportButton()}
          {fullscreenButton()}
        </>
    };
    return <OverflowSetCustomExample component={overFlowComponent} />
  };

  const oAuth_Saml_Data = () => {
    const authTypeData = () => (
      <Stack horizontal tokens={{ childrenGap: 10 }}>
        <StackItem>
          <Stack tokens={{ childrenGap: 5 }}>
            <StackItem><Text /></StackItem>
            <StackItem> <Body1> Authentication Type : </Body1> </StackItem>
          </Stack>
        </StackItem>
        <StackItem>
          <Stack horizontal tokens={{ childrenGap: 10 }} role="radiogroup" aria-label="Authentication Type">
            <Radio label="OAUTH" name="authType" checked={selectedAuthType === 'OAUTH'} onChange={() => handleAuthTypeChange('OAUTH')} />
            <Radio label="SAML" name="authType" checked={selectedAuthType === 'SAML'} onChange={() => handleAuthTypeChange('SAML')} />
          </Stack>
        </StackItem>
        <StackItem> <Divider vertical /> <Divider vertical /> </StackItem>
        <StackItem>
          <Switch labelPosition="before" label={"Enable Single Sign-On"} onChange={handleSSOToggle} checked={formData?.sso_status === 1} disabled={isDisabled} />
        </StackItem>
      </Stack>
    );

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      (selectedAuthType !== 'SAML')
        ? setFormData(prevData => ({ ...prevData, [name]: value }))
        : setFormData(prevData => ({ ...prevData, [name]: value }));
    };

    const {
      company_name: oAuthCompany, idp_full_name: oAuthIDP, client_id: oAuthID, client_secret: oAuthSecret, scope: oAuthScope, oidc_endpoint: oAuthOIDC
    } = formData || {};
    const { company_name: samlCompany, idp_full_name: samlIDP, client_id: samlID, client_secret: samlSecret } = SAMLFormData || {};

    const iconTootltip = (text: string) => (
      <Tooltip withArrow positioning="after" relationship="description" content={text}>
        <Info16Filled {...primaryIcon} />
      </Tooltip>
    );

    const oAuthData = () => selectedAuthType === 'OAUTH' && (
      <Stack tokens={{ childrenGap: 15 }} style={formOverflowDivider}>
        <Body1Stronger> OAuth Configuration </Body1Stronger>
        <Text />
        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{ childrenGap: 5 }} horizontal>
            <Body1Strong> Company Name </Body1Strong>
            {iconTootltip("The name of the organization utilizing the Identity Provider (IdP) for managing authentication and user access.")}
          </Stack>
          <TextField name="company_name" value={oAuthCompany} onChange={handleInputChange} />
        </Stack>

        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{ childrenGap: 5 }} horizontal>
            <Body1Strong> IdP Full Name </Body1Strong>
            {iconTootltip("The full name of the Identity Provider, such as Okta, Azure AD.")}
          </Stack>
          <TextField name="idp_full_name" value={oAuthIDP} onChange={handleInputChange} />
        </Stack>

        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{ childrenGap: 5 }} horizontal>
            <Body1Strong> Client ID </Body1Strong>
            {iconTootltip("A unique identifier assigned to the service provider (SP) or application, enabling the IdP to recognize and process authentication requests for that specific application.")}
          </Stack>
          <TextField name="client_id" value={oAuthID} onChange={handleInputChange} />
        </Stack>

        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{ childrenGap: 5 }} horizontal>
            <Body1Strong> Client Secret </Body1Strong>
            {iconTootltip("A secure key paired with the Client ID for authenticating the application.")}
          </Stack>
          <TextField name="client_secret" value={oAuthSecret} onChange={handleInputChange} type="text" />
        </Stack>

        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{ childrenGap: 5 }} horizontal>
            <Body1Strong> Scope (comma / space delimited) </Body1Strong>
            {iconTootltip("Outlines the permissions or access levels the application requests.")}
          </Stack>
          <TextField name="scope" value={oAuthScope} onChange={handleInputChange} />
        </Stack>

        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{ childrenGap: 5 }} horizontal>
            <Body1Strong> OIDC Endpoint </Body1Strong>
            {iconTootltip("The access point provided by the IdP for secure operations such as authenticating users, generating tokens, and retrieving identity-related information.")}
          </Stack>
          <TextField name="oidc_endpoint" value={oAuthOIDC} onChange={handleInputChange} />
        </Stack>

      </Stack>
    );

    const samlData = () => selectedAuthType === 'SAML' && (
      <Stack tokens={{ childrenGap: 10 }} style={{ overflow: 'auto' }}>
        <Body1Stronger> SAML Configuration </Body1Stronger>
        <Text/>
        
        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{childrenGap:5}} horizontal>
            <Body1Strong> Company Name </Body1Strong>
            {iconTootltip("The name of the organization utilizing the Identity Provider (IdP) for managing authentication and user access.")}
          </Stack>
          <TextField name="company_name" value={samlCompany} onChange={handleInputChange} />
        </Stack>

        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{childrenGap:5}} horizontal>
            <Body1Strong> IdP Full Name </Body1Strong>
            {iconTootltip("The full name of the Identity Provider, such as Okta, Azure AD.")}
          </Stack>
          <TextField label="" name="idp_full_name" value={samlIDP} onChange={handleInputChange} />
        </Stack>

        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{childrenGap:5}} horizontal>
            <Body1Strong> Client ID </Body1Strong>
            {iconTootltip("A unique identifier assigned to the service provider (SP) or application, enabling the IdP to recognize and process authentication requests for that specific application.")}
          </Stack>
          <TextField name="client_id" value={samlID} onChange={handleInputChange} />
        </Stack>

        <Stack tokens={{ childrenGap: 5 }}>
          <Stack tokens={{childrenGap:5}} horizontal>
            <Body1Strong> Client Secret </Body1Strong>
            {iconTootltip("A unique identifier assigned to the service provider (SP) or application, enabling the IdP to recognize and process authentication requests for that specific application.")}
          </Stack>
          <TextField  name="client_secret" value={samlSecret} onChange={handleInputChange} type="text" />
        </Stack>

      </Stack>
    );

    const formButtons = () => (
      <Stack horizontal tokens={{ childrenGap: 20 }}>
        <StackItem>
          <Button shape="circular" appearance="primary" onClick={handleSubmit} disabled={isSubmitDisabled}> Save </Button>
        </StackItem>
        <StackItem>
          <StackItem>
            <DialogBox
              headerButton={<Button shape="circular" appearance="outline" disabled={isDisabled}> Clear </Button>}
              content="Are you sure you want to clear SSO configuration"
              footer={
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                  <StackItem>
                    <Button shape="circular" appearance="primary" size="small" onClick={() => { handleClear(); setIsLoading(true); }}>
                      Confirm
                    </Button>
                  </StackItem>
                  <StackItem> <Button shape="circular" size="small"> Cancel </Button> </StackItem>
                </Stack>
              }
            />
          </StackItem>
        </StackItem>
      </Stack>
    );

    return (
      <Stack tokens={{ childrenGap: 10, padding: "0px 0px 0px 10px" }} style={{ ...tableDiv, ...heightResponsive }}>
        {authTypeData()}
        {oAuthData()}
        {samlData()}
        {isToast && <ToastComponent setmessagebar={setIsToast} isToastEnabled={isToast} toastIntent={message.type} position="top-end" title={message.type.charAt(0).toUpperCase() + message.type.slice(1)} message={message.text} footer="" />}
        <StackItem><Text /></StackItem> <StackItem><Text /></StackItem>
        {formButtons()}
        <StackItem><Text /></StackItem> <StackItem><Text /></StackItem>
      </Stack>
    );
  };


  return (
    <FullScreen handle={handle} className="fullScreenBackgroundColor">
      {breadcrumbData()}
      <Stack tokens={{ childrenGap: 20 }} style={SubSectionResponsive} horizontal>
        <StackItem style={DynamicBoxingCSS}>
          <Stack style={DynamicBoxingCSS}>
            <FluentProvider style={parentHome} theme={dashboardTheme}>
              <Stack style={parentHome} horizontal>
                {sidebarData()}
                <StackItem style={DynamicBoxingCSS}>
                  {overflowData()}
                  <Divider />
                  {isLoading ? <Stack style={heightResponsive}> <SkeletonDataTable /> </Stack> : oAuth_Saml_Data()}
                </StackItem>
              </Stack>
            </FluentProvider>
            {refreshToast && <ToastComponent setmessagebar={setIsToast} isToastEnabled={refreshToast} toastIntent="success" position="top-end" title="Success" message={'Data Table Refreshed'} footer="" />}
          </Stack>
        </StackItem>
        <StackItem><Text /></StackItem>
      </Stack>
    </FullScreen>
  );
};
